import React, { useCallback, useEffect, useState } from 'react';
import api from '@/api/engine';
import {
  DangerButton,
  DropdownSetting,
  PanelShell,
  PanelSkeleton,
  SettingsSection,
  SliderSetting,
  ToggleSetting,
  UpgradeNudge,
  normalizeError,
  useSettingsContext,
  type NormalizedError,
  type SaveStatus,
  type TierLevel,
} from '@seitiate/settings-ui';

/**
 * GovernorPanel — Quality enforcement (renamed from Machine Room).
 *
 * Surfaces the full N1–N5 failure taxonomy, ORA loop, Canon tie-in,
 * and 6 kill switches per SETTINGS-PANEL-DETAILS-V1.md §3.
 *
 * Backend contract: 6 stable fields in the `governor` category plus 9
 * planned-not-yet-built fields that render as `blueprint-planned`. The
 * 9 planned fields materialize once the Elixir Governor ships.
 */

type StrictnessValue = 'relaxed' | 'standard' | 'strict';

interface GovernorStableFields {
  n1_strictness: StrictnessValue;
  n2_strictness: StrictnessValue;
  ora_max_iterations: number;
  kill_switch_arc_wholeness: number;
  kill_switch_character_integration: number;
  kill_switch_setup_payoff_distance: number;
}

const STRICTNESS_OPTIONS = [
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'standard', label: 'Standard' },
  { value: 'strict', label: 'Strict' },
];

const PRESET_OPTIONS = [
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'standard', label: 'Standard' },
  { value: 'strict', label: 'Strict' },
  { value: 'custom', label: 'Custom' },
];

type PresetKey = 'relaxed' | 'standard' | 'strict';

const PRESETS: Record<PresetKey, GovernorStableFields> = {
  relaxed: {
    n1_strictness: 'relaxed',
    n2_strictness: 'relaxed',
    ora_max_iterations: 1,
    kill_switch_arc_wholeness: 2,
    kill_switch_character_integration: 0.4,
    kill_switch_setup_payoff_distance: 10,
  },
  standard: {
    n1_strictness: 'standard',
    n2_strictness: 'standard',
    ora_max_iterations: 3,
    kill_switch_arc_wholeness: 3,
    kill_switch_character_integration: 0.5,
    kill_switch_setup_payoff_distance: 8,
  },
  strict: {
    n1_strictness: 'strict',
    n2_strictness: 'strict',
    ora_max_iterations: 5,
    kill_switch_arc_wholeness: 5,
    kill_switch_character_integration: 0.75,
    kill_switch_setup_payoff_distance: 5,
  },
};

// Accepts the legacy TierLevel ladder AND the new Free / Amateur / Professional
// platform base (Option-B re-home). SettingsPage already normalizes a platform
// tier onto a TierLevel before it reaches the context, so the legacy names are
// the common path; the new keys are here so a raw platform tier still gates
// correctly. professional ranks with mastery (unlocks the kill switches).
const TIER_RANK: Record<string, number> = {
  sense: 0,
  making: 1,
  meaning: 2,
  mastery: 3,
  sovereign: 4,
  free: 0,
  amateur: 1,
  professional: 3,
};

function meetsTier(userTier: TierLevel | string | undefined, required: TierLevel): boolean {
  if (!userTier) return false;
  return (TIER_RANK[userTier] ?? 0) >= (TIER_RANK[required] ?? 0);
}

function detectPreset(fields: GovernorStableFields): PresetKey | 'custom' {
  for (const key of ['relaxed', 'standard', 'strict'] as const) {
    const p = PRESETS[key];
    if (
      fields.n1_strictness === p.n1_strictness &&
      fields.n2_strictness === p.n2_strictness &&
      fields.ora_max_iterations === p.ora_max_iterations &&
      fields.kill_switch_arc_wholeness === p.kill_switch_arc_wholeness &&
      Math.abs(fields.kill_switch_character_integration - p.kill_switch_character_integration) < 0.001 &&
      fields.kill_switch_setup_payoff_distance === p.kill_switch_setup_payoff_distance
    ) {
      return key;
    }
  }
  return 'custom';
}

export const GovernorPanel: React.FC<{ mode: 'admin' | 'tenant' }> = () => {
  const { tier, expertMode } = useSettingsContext();
  const userTier = (tier ?? undefined) as TierLevel | undefined;

  const [fields, setFields] = useState<GovernorStableFields | null>(null);
  const [visible, setVisible] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<NormalizedError | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ state: 'idle' });

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const overview = await api.settings.getOverview();
      setVisible(overview.visible_categories || []);
      if (overview.visible_categories?.includes('governor')) {
        const response = await api.settings.getCategory('governor');
        setFields(response.settings as unknown as GovernorStableFields);
      } else {
        setFields(null);
      }
    } catch (err) {
      setLoadError(normalizeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const saveFields = useCallback(async (next: Partial<GovernorStableFields>) => {
    setSaveStatus({ state: 'saving' });
    try {
      const response = await api.settings.updateCategory('governor', next as Record<string, unknown>);
      setFields(response.settings as unknown as GovernorStableFields);
      setSaveStatus({ state: 'saved', at: Date.now() });
    } catch (err) {
      const normalized = normalizeError(err);
      setSaveStatus({
        state: 'failed',
        message: normalized.message,
        onRetry: () => void saveFields(next),
      });
    }
  }, []);

  const updateField = <K extends keyof GovernorStableFields>(key: K, value: GovernorStableFields[K]) => {
    void saveFields({ [key]: value } as Partial<GovernorStableFields>);
  };

  const applyPreset = (preset: PresetKey) => {
    void saveFields(PRESETS[preset]);
  };

  if (loading) {
    return (
      <PanelShell title="Governor" description="Quality enforcement — how strictly your seity holds itself to your canon and structural gates.">
        <PanelSkeleton rows={6} />
      </PanelShell>
    );
  }

  if (loadError) {
    return (
      <PanelShell
        title="Governor"
        description="Quality enforcement — how strictly your seity holds itself to your canon and structural gates."
        error={loadError}
        onRetryError={() => void loadData()}
      >
        <div />
      </PanelShell>
    );
  }

  const hasGovernor = visible.includes('governor') && !!fields;
  const meaningPlus = meetsTier(userTier, 'meaning');
  const masteryPlus = meetsTier(userTier, 'mastery');
  const sovereignPlus = meetsTier(userTier, 'sovereign');
  const makingPlus = meetsTier(userTier, 'making');

  if (!hasGovernor || !fields) {
    return (
      <PanelShell
        title="Governor"
        description="Quality enforcement — how strictly your seity holds itself to your canon and structural gates."
        tierBadge="Meaning+"
        showExpertToggle
      >
        <UpgradeNudge requiredTier="Meaning+" feature="Governor settings" />
      </PanelShell>
    );
  }

  const activePreset = detectPreset(fields);

  return (
    <PanelShell
      title="Governor"
      description="Quality enforcement — how strictly your seity holds itself to your canon and structural gates."
      tierBadge="Meaning+"
      showExpertToggle
      saveStatus={saveStatus}
      showRefresh
      onRefresh={() => void loadData()}
    >
      {/* A. Preset — Simple, always visible */}
      <SettingsSection title="Preset" anchorId="section-standards-governor-preset">
        <DropdownSetting
          anchorId="section-standards-governor-preset-dropdown"
          label="Governance preset"
          value={activePreset}
          options={PRESET_OPTIONS}
          onChange={(value) => {
            if (value === 'custom') return;
            applyPreset(value as PresetKey);
          }}
          description="Relaxed / Standard / Strict overwrite N1–N5, ORA iterations, and kill switches together. Switching off a preset keeps your current values as Custom."
        />
      </SettingsSection>

      {/* B. Strictness — N1/N2 Making+, N3/N4/N5 Meaning+ (blueprint-planned) */}
      <SettingsSection title="Strictness" anchorId="section-standards-strictness">
        <DropdownSetting
          anchorId="governor-n1_strictness"
          label="N1 — Character contamination"
          value={fields.n1_strictness}
          options={STRICTNESS_OPTIONS}
          onChange={(value) => updateField('n1_strictness', value as StrictnessValue)}
          description="Catches characters slipping into another character's voice."
          state={makingPlus ? undefined : 'tier-gated'}
          requiredTier="Making+"
          feature="N1 strictness"
        />
        <DropdownSetting
          anchorId="governor-n2_strictness"
          label="N2 — Continuity break"
          value={fields.n2_strictness}
          options={STRICTNESS_OPTIONS}
          onChange={(value) => updateField('n2_strictness', value as StrictnessValue)}
          description="Catches contradictions against earlier scenes or established facts."
          state={makingPlus ? undefined : 'tier-gated'}
          requiredTier="Making+"
          feature="N2 strictness"
        />
        <DropdownSetting
          anchorId="governor-n3_strictness"
          label="N3 — Arc violation"
          value="standard"
          options={STRICTNESS_OPTIONS}
          onChange={() => {}}
          description="Flags when a scene breaks the promised arc shape."
          state={meaningPlus ? 'blueprint-planned' : 'tier-gated'}
          requiredTier="Meaning+"
          feature="N3 strictness"
        />
        <DropdownSetting
          anchorId="governor-n4_strictness"
          label="N4 — Voice drift"
          value="standard"
          options={STRICTNESS_OPTIONS}
          onChange={() => {}}
          description="Flags narrator voice drifting from the calibrated baseline."
          state={meaningPlus ? 'blueprint-planned' : 'tier-gated'}
          requiredTier="Meaning+"
          feature="N4 strictness"
        />
        <DropdownSetting
          anchorId="governor-n5_strictness"
          label="N5 — Pacing failure"
          value="standard"
          options={STRICTNESS_OPTIONS}
          onChange={() => {}}
          description="Flags scenes that stall or rush against the declared cadence."
          state={meaningPlus ? 'blueprint-planned' : 'tier-gated'}
          requiredTier="Meaning+"
          feature="N5 strictness"
        />
      </SettingsSection>

      {/* C. ORA Loop — Meaning+ */}
      <SettingsSection title="ORA loop" anchorId="section-standards-ora">
        <SliderSetting
          anchorId="governor-ora_max_iterations"
          label="Max ORA iterations"
          value={fields.ora_max_iterations}
          min={1}
          max={5}
          onChange={(v) => updateField('ora_max_iterations', v)}
          description="Maximum Observe-Reason-Act correction cycles before a generation is held for human review."
          state={meaningPlus ? undefined : 'tier-gated'}
          requiredTier="Meaning+"
          feature="ORA iterations"
        />
        <div id="section-standards-ora-metrics-link" className="mt-2 text-xs leading-5" style={{ color: 'var(--text-secondary)' }}>
          ORA metrics review:{' '}
          <a
            href="/settings/agents#section-agents-ora-metrics"
            className="underline"
            style={{ color: 'var(--accent-gold)' }}
          >
            Open primitives cockpit
          </a>
        </div>
      </SettingsSection>

      {/* D. Canon tie-in — Expert + Meaning+ (all blueprint-planned) */}
      {expertMode ? (
        <SettingsSection title="Canon tie-in" anchorId="section-standards-canon-tie-in">
          <ToggleSetting
            anchorId="governor-canon_evaluation_enabled"
            label="Canon evaluation enabled"
            checked={true}
            onChange={() => {}}
            description="Evaluate generations against Canon assertions before they pass the Governor."
            state={meaningPlus ? 'blueprint-planned' : 'tier-gated'}
            requiredTier="Meaning+"
            feature="Canon evaluation"
          />
          <SliderSetting
            anchorId="governor-canon_weight"
            label="Canon weight"
            value={0.4}
            min={0}
            max={1}
            step={0.05}
            onChange={() => {}}
            formatValue={(v) => v.toFixed(2)}
            description="How heavily Canon alignment factors into Governor decisions (0 = ignore, 1 = dominant)."
            state={meaningPlus ? 'blueprint-planned' : 'tier-gated'}
            requiredTier="Meaning+"
            feature="Canon weight"
          />
          <SliderSetting
            anchorId="governor-hitl_threshold"
            label="HITL threshold"
            value={0.5}
            min={0}
            max={1}
            step={0.05}
            onChange={() => {}}
            formatValue={(v) => v.toFixed(2)}
            description="Below this confidence, a generation is held for human review instead of shipping through ORA."
            state={meaningPlus ? 'blueprint-planned' : 'tier-gated'}
            requiredTier="Meaning+"
            feature="HITL threshold"
          />
        </SettingsSection>
      ) : null}

      {/* E. Kill switches — Expert + Mastery+ (base) / Sovereign+ (extended) */}
      {expertMode ? (
        <SettingsSection title="Kill switches" anchorId="section-standards-kill-switches">
          <KillSwitchRow
            anchorId="governor-kill_switch_arc_wholeness"
            label="Arc wholeness (beats per act)"
            value={fields.kill_switch_arc_wholeness}
            min={1}
            max={7}
            onChange={(v) => updateField('kill_switch_arc_wholeness', v)}
            description="Minimum beats per act before the arc is flagged as incomplete."
            state={masteryPlus ? undefined : 'tier-gated'}
            requiredTier="Mastery+"
            feature="Arc wholeness kill switch"
          />
          <KillSwitchRow
            anchorId="governor-kill_switch_character_integration"
            label="Character integration"
            value={fields.kill_switch_character_integration}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => updateField('kill_switch_character_integration', v)}
            formatValue={(v) => v.toFixed(2)}
            description="Minimum character connectivity across scenes."
            state={masteryPlus ? undefined : 'tier-gated'}
            requiredTier="Mastery+"
            feature="Character integration kill switch"
          />
          <KillSwitchRow
            anchorId="governor-kill_switch_setup_payoff_distance"
            label="Setup→payoff distance (scenes)"
            value={fields.kill_switch_setup_payoff_distance}
            min={1}
            max={10}
            onChange={(v) => updateField('kill_switch_setup_payoff_distance', v)}
            description="Maximum scenes between a setup and its payoff."
            state={masteryPlus ? undefined : 'tier-gated'}
            requiredTier="Mastery+"
            feature="Setup-payoff kill switch"
          />
          <KillSwitchRow
            anchorId="governor-kill_switch_thread_balance"
            label="Thread balance"
            value={0.6}
            min={0}
            max={1}
            step={0.05}
            onChange={() => {}}
            formatValue={(v) => v.toFixed(2)}
            description="Minimum balance across plot threads before the draft is flagged."
            state={sovereignPlus ? 'blueprint-planned' : 'tier-gated'}
            requiredTier="Sovereign"
            feature="Thread balance kill switch"
          />
          <KillSwitchRow
            anchorId="governor-kill_switch_word_count_deviation"
            label="Word count deviation"
            value={0.15}
            min={0.05}
            max={0.5}
            step={0.05}
            onChange={() => {}}
            formatValue={(v) => `±${(v * 100).toFixed(0)}%`}
            description="Maximum deviation from the declared word-count target before the draft is flagged."
            state={sovereignPlus ? 'blueprint-planned' : 'tier-gated'}
            requiredTier="Sovereign"
            feature="Word count deviation kill switch"
          />
          <KillSwitchRow
            anchorId="governor-kill_switch_beat_coverage"
            label="Beat coverage"
            value={0.8}
            min={0}
            max={1}
            step={0.05}
            onChange={() => {}}
            formatValue={(v) => v.toFixed(2)}
            description="Minimum coverage of the declared beats before the draft is flagged."
            state={sovereignPlus ? 'blueprint-planned' : 'tier-gated'}
            requiredTier="Sovereign"
            feature="Beat coverage kill switch"
          />
        </SettingsSection>
      ) : null}
    </PanelShell>
  );
};

interface KillSwitchRowProps {
  anchorId: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  formatValue?: (v: number) => string;
  description?: string;
  state?: 'tier-gated' | 'blueprint-planned';
  requiredTier: string;
  feature: string;
}

const KillSwitchRow: React.FC<KillSwitchRowProps> = ({
  anchorId,
  label,
  value,
  min,
  max,
  step,
  onChange,
  formatValue,
  description,
  state,
  requiredTier,
  feature,
}) => {
  const isLive = state === undefined;
  return (
    <div className="space-y-2">
      <SliderSetting
        anchorId={anchorId}
        label={label}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={onChange}
        formatValue={formatValue}
        description={description}
        state={state}
        requiredTier={requiredTier}
        feature={feature}
      />
      {isLive ? (
        <div className="pl-1">
          <DangerButton
            label="Override for this session"
            confirmText={`Override the ${label.toLowerCase()} kill switch for this session only? It will re-engage next session.`}
            onConfirm={() => {
              /* session-scoped override is a client-side flag; wiring tracked in SETTINGS-KILL-SWITCH-OVERRIDE-1 */
            }}
            reasonRequired
            reasonLabel="Reason for override"
          />
        </div>
      ) : null}
    </div>
  );
};
